import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';

// Import KaTeX CSS
import 'katex/dist/katex.min.css';

// Error boundary for KaTeX
const KaTeXErrorBoundary: React.FC<{ children: React.ReactNode; fallback?: string }> = ({
  children,
  fallback = 'Math rendering error'
}) => {
  try {
    return <>{children}</>;
  } catch (error) {
    return <span style={{ color: 'red' }}>[{fallback}]</span>;
  }
};

interface ContentRendererProps {
  text: string;
  display?: 'inline' | 'block';
}

const ContentRenderer: React.FC<ContentRendererProps> = ({ text, display = 'inline' }) => {
  // Safety check
  if (!text) {
    return display === 'block' ? <div>No content</div> : <span>No content</span>;
  }

  if (typeof text !== 'string') {
    const stringText = String(text);
    return display === 'block' ? <div>{stringText}</div> : <span>{stringText}</span>;
  }

  // Hàm để kiểm tra xem text có chứa ký hiệu toán học LaTeX không
  const hasMathContent = (text: string): boolean => {
    if (!text || typeof text !== 'string') return false;
    // Kiểm tra cả delimiter và raw LaTeX commands
    const mathRegex = /(\$\$.*?\$\$|\$.*?\$|\\\(.*?\\\)|\\\[.*?\\\]|\\[a-zA-Z]+|\\frac|\\sqrt|\\sum|\\int|\\lim|\\sin|\\cos|\\tan|\\log|\\ln|\\exp|\\pi|\\alpha|\\beta|\\gamma|\\delta|\\epsilon|\\theta|\\lambda|\\mu|\\sigma|\\tau|\\phi|\\omega|\\infty|\\partial|\\nabla|\\times|\\div|\\pm|\\mp|\\leq|\\geq|\\neq|\\approx|\\equiv|\\propto|\\in|\\notin|\\subset|\\supset|\\cup|\\cap|\\emptyset|\\varnothing|\\rightarrow|\\leftarrow|\\leftrightarrow|\\Rightarrow|\\Leftarrow|\\Leftrightarrow|\\forall|\\exists|\\neg|\\land|\\lor|\\wedge|\\vee|\\oplus|\\ominus|\\otimes|\\odot|\\bigcup|\\bigcap|\\bigoplus|\\bigotimes|\\bigodot|\\prod|\\iint|\\iiint|\\oint|\\limsup|\\liminf|\\sup|\\inf|\\max|\\min|\\gcd|\\lcm|\\det|\\dim|\\ker|\\im|\\rank|\\trace|\\adj|\\cof|\\tr|\\span|\\null|\\range|\\domain|\\codomain|\\image|\\preimage|\\inverse|\\complement|\\interior|\\exterior|\\boundary|\\closure|\\derivative|\\gradient|\\divergence|\\curl|\\laplacian|\\hessian|\\jacobian|\\wronskian|\\discriminant|\\residue|\\pole|\\zero|\\one|\\two|\\three|\\four|\\five|\\six|\\seven|\\eight|\\nine|\\ten|\\hundred|\\thousand|\\million|\\billion|\\trillion|\\quadrillion|\\quintillion|\\sextillion|\\septillion|\\octillion|\\nonillion|\\decillion|\\undecillion|\\duodecillion|\\tredecillion|\\quattuordecillion|\\quindecillion|\\sexdecillion|\\septendecillion|\\octodecillion|\\novemdecillion|\\vigintillion|\\mathbb\{[^}]+\}|\\mathcal\{[^}]+\}|\\mathfrak\{[^}]+\}|\\mathrm\{[^}]+\}|\\mathbf\{[^}]+\}|\\mathit\{[^}]+\}|\\text\{[^}]+\}|\\textbf\{[^}]+\}|\\textit\{[^}]+\}|\\emph\{[^}]+\}|\\{|\}|\(|\)|\[|\]|\^|_)/g;
    return mathRegex.test(text);
  };

  // Hàm để kiểm tra xem text có chứa HTML tags không
  const hasHtmlContent = (text: string): boolean => {
    if (!text || typeof text !== 'string') return false;
    const htmlRegex = /<[^>]*>/g;
    return htmlRegex.test(text);
  };

  // Hàm để tự động wrap raw LaTeX expressions với delimiter
  const wrapRawLatex = (text: string): string => {
    if (!text || typeof text !== 'string') return text;

    // Trước tiên, tìm và xử lý các LaTeX expressions đã có delimiter
    // Tách text thành các phần: có delimiter và không có delimiter
    const parts = text.split(/(\$\$.*?\$\$|\$.*?\$|\\\(.*?\\\)|\\\[.*?\\\])/g);

    return parts.map(part => {
      // Nếu phần này đã có delimiter, giữ nguyên
      if (part.startsWith('$$') && part.endsWith('$$')) {
        return part;
      }
      if (part.startsWith('$') && part.endsWith('$')) {
        return part;
      }
      if (part.startsWith('\\(') && part.endsWith('\\)')) {
        return part;
      }
      if (part.startsWith('\\[') && part.endsWith('\\]')) {
        return part;
      }

      // Nếu phần này không có delimiter, tìm và wrap các LaTeX commands và math expressions
      // Bao gồm cả LaTeX commands và các ký hiệu toán học thông thường
      // Chỉ xử lý các hàm toán học khi có context rõ ràng (có dấu ngoặc đơn)
      const latexRegex = /(\\[a-zA-Z]+\{[^}]*\}(?:\{[^}]*\})*(?:\s*[+\-*/=<>≤≥≠≈≡∝±∓×÷]?\s*[a-zA-Z0-9\\{}^_()[\]]*)*|\\[a-zA-Z]+|\\[^a-zA-Z\s$]|[a-zA-Z]'\([^)]*\)|\([^)]*\)\^[0-9]+|\([^)]*\)\^[a-zA-Z]+|[a-zA-Z]+\^[0-9]+|[a-zA-Z]+\^[a-zA-Z]+|sqrt\([^)]*\)|sin\([^)]*\)|cos\([^)]*\)|tan\([^)]*\)|log\([^)]*\)|ln\([^)]*\)|exp\([^)]*\)|[0-9]+[ˣᵃᵇᶜᵈᵉᶠᵍʰⁱʲᵏˡᵐⁿᵒᵖʳˢᵗᵘᵛʷˣʸᶻ]|[a-zA-Z]+[ˣᵃᵇᶜᵈᵉᶠᵍʰⁱʲᵏˡᵐⁿᵒᵖʳˢᵗᵘᵛʷˣʸᶻ]|ln[0-9]+|\\forall|\\exists|\\Rightarrow|\\Leftarrow|\\Leftrightarrow|\\rightarrow|\\leftarrow|\\leftrightarrow|\\in|\\notin|\\subset|\\supset|\\subseteq|\\supseteq|\\cup|\\cap|\\emptyset|\\varnothing|\\mathbb\{[^}]+\}|\\mathcal\{[^}]+\}|\\mathfrak\{[^}]+\}|\\mathrm\{[^}]+\}|\\mathbf\{[^}]+\}|\\mathit\{[^}]+\}|\\text\{[^}]+\}|\\textbf\{[^}]+\}|\\textit\{[^}]+\}|\\emph\{[^}]+\}|\\neg|\\land|\\lor|\\wedge|\\vee|\\oplus|\\ominus|\\otimes|\\odot|\\bigcup|\\bigcap|\\bigoplus|\\bigotimes|\\bigodot|\\sum|\\prod|\\int|\\iint|\\iiint|\\oint|\\lim|\\limsup|\\liminf|\\sup|\\inf|\\max|\\min|\\gcd|\\lcm|\\det|\\dim|\\ker|\\im|\\rank|\\trace|\\adj|\\cof|\\tr|\\span|\\null|\\range|\\domain|\\codomain|\\image|\\preimage|\\inverse|\\complement|\\interior|\\exterior|\\boundary|\\closure|\\derivative|\\partial|\\gradient|\\divergence|\\curl|\\laplacian|\\hessian|\\jacobian|\\wronskian|\\discriminant|\\residue|\\pole|\\zero|\\one|\\two|\\three|\\four|\\five|\\six|\\seven|\\eight|\\nine|\\ten|\\hundred|\\thousand|\\million|\\billion|\\trillion|\\quadrillion|\\quintillion|\\sextillion|\\septillion|\\octillion|\\nonillion|\\decillion|\\undecillion|\\duodecillion|\\tredecillion|\\quattuordecillion|\\quindecillion|\\sexdecillion|\\septendecillion|\\octodecillion|\\novemdecillion|\\vigintillion)/g;

      return part.replace(latexRegex, (match) => {
        // Wrap với $ delimiter cho inline math
        return `$${match}$`;
      });
    }).join('');
  };

  // Hàm để tách và render toán học từ text
  const renderMathText = (text: string) => {
    // Trước tiên, wrap raw LaTeX expressions với delimiter
    const wrappedText = wrapRawLatex(text);

    // Regex để tìm các ký hiệu toán học LaTeX
    const mathRegex = /(\$\$.*?\$\$|\$.*?\$|\\\(.*?\\\)|\\\[.*?\\\])/g;
    const parts = wrappedText.split(mathRegex);

    return parts.map((part, index) => {
      // Kiểm tra xem có phải là toán học không
      if (part.startsWith('$$') && part.endsWith('$$')) {
        // Block math
        const mathContent = part.slice(2, -2);
        return (
          <KaTeXErrorBoundary key={index} fallback={`Block math: ${mathContent}`}>
            <BlockMath math={mathContent} />
          </KaTeXErrorBoundary>
        );
      } else if (part.startsWith('$') && part.endsWith('$')) {
        // Inline math
        const mathContent = part.slice(1, -1);
        return (
          <KaTeXErrorBoundary key={index} fallback={`Math: ${mathContent}`}>
            <InlineMath math={mathContent} />
          </KaTeXErrorBoundary>
        );
      } else if (part.startsWith('\\(') && part.endsWith('\\)')) {
        // Inline math với \( \)
        const mathContent = part.slice(2, -2);
        return (
          <KaTeXErrorBoundary key={index} fallback={`Math: ${mathContent}`}>
            <InlineMath math={mathContent} />
          </KaTeXErrorBoundary>
        );
      } else if (part.startsWith('\\[') && part.endsWith('\\]')) {
        // Block math với \[ \]
        const mathContent = part.slice(2, -2);
        return (
          <KaTeXErrorBoundary key={index} fallback={`Block math: ${mathContent}`}>
            <BlockMath math={mathContent} />
          </KaTeXErrorBoundary>
        );
      } else {
        // Text thường - kiểm tra xem có HTML không
        if (hasHtmlContent(part)) {
          return <span key={index} dangerouslySetInnerHTML={{ __html: part }} />;
        }
        return <span key={index}>{part}</span>;
      }
    });
  };

  // Luôn wrap raw LaTeX trước khi xử lý
  const processedText = wrapRawLatex(text);

  // Kiểm tra lại sau khi wrap
  const hasMathAfterWrap = hasMathContent(processedText);
  const hasHtmlAfterWrap = hasHtmlContent(processedText);

  // Nếu không có ký hiệu toán học và HTML, trả về text thường
  if (!hasMathAfterWrap && !hasHtmlAfterWrap) {
    if (display === 'block') {
      return <div>{processedText}</div>;
    }
    return <span>{processedText}</span>;
  }

  // Nếu chỉ có HTML mà không có LaTeX
  if (!hasMathAfterWrap && hasHtmlAfterWrap) {
    if (display === 'block') {
      return <div dangerouslySetInnerHTML={{ __html: processedText }} />;
    }
    return <span dangerouslySetInnerHTML={{ __html: processedText }} />;
  }

  // Nếu có cả LaTeX và HTML, hoặc chỉ có LaTeX
  if (display === 'block') {
    return <div>{renderMathText(processedText)}</div>;
  }

  // Inline display
  return <span>{renderMathText(processedText)}</span>;
};

export default ContentRenderer; 